/**
 * Spec 34 — adapter from the existing ``ResolvedInventoryDTO``
 * (the wire shape returned by ``GET /api/library/{id}/inventory``)
 * to the narrow ``InventoryView`` the unified Hub merger expects.
 *
 * Pure function — no I/O, no SDK. Tested in isolation.
 *
 * The DTO carries:
 *   - classifications[]  (the strategy stack output, plus M1a's
 *     processing_status + parsed fields)
 *   - clusters[]         (multiplicity — out of scope for Hub view)
 *   - source_classifications[]  (Spec 32 production-harness output;
 *     empty when the doctype hasn't onboarded source-doctyping)
 *
 * The InventoryView shape needs ``documents`` to seed row identity.
 * The DTO doesn't carry a separate document list — every classified
 * doc has its name + id in the classification entry, so we
 * synthesise the document list from that.
 */

import type { ResolvedInventoryDTO } from "../../api/client";
import type { InventoryView } from "./types";

export function inventoryDtoToView(dto: ResolvedInventoryDTO | null): InventoryView {
  if (!dto) {
    return {
      documents: [],
      classifications: [],
      source_classifications: [],
    };
  }

  // Document list synthesised from the classification entries —
  // each one carries doc_id + doc_name + the M1a processing fields.
  // Order preservation matters: the merger uses the documents list
  // as the row order.
  const documents = dto.classifications.map((c) => ({
    id: c.doc_id,
    name: c.doc_name,
    file_type: undefined,
    processing_status: c.processing_status ?? undefined,
    parsed: c.parsed ?? undefined,
  }));

  // Classifications mapped 1:1; the InventoryView's
  // InventoryClassificationLite is a strict subset of
  // ResolvedClassification with ``confidence`` narrowed to the
  // four-value enum the Hub UI handles.
  const classifications = dto.classifications.map((c) => ({
    doc_id: c.doc_id,
    doc_name: c.doc_name,
    doc_type: c.doc_type,
    confidence: normaliseConfidence(c.confidence),
    processing_status: c.processing_status ?? null,
    parsed: c.parsed ?? null,
    verified: c.verified ?? false,
    verified_by: c.verified_by,
    verified_at: c.verified_at,
    label_params: c.label_params ?? null,
  }));

  const source_classifications = (dto.source_classifications ?? []).map((s) => ({
    doc_id: s.doc_id,
    source_doctype: s.source_doctype,
    tags: s.tags,
    confidence: s.confidence,
    rationale: s.rationale,
    error: s.error,
  }));

  return { documents, classifications, source_classifications };
}

function normaliseConfidence(raw: string): "high" | "medium" | "low" | "unknown" {
  if (raw === "high" || raw === "medium" || raw === "low") return raw;
  return "unknown";
}
